import type {
	NewSearchSuggestProps,
	Suggestion
} from '~/components/SearchSuggest';
import type { ComputedRef, Ref } from 'vue';

/**
 * Trigger do modal quando o texto exibido prioriza o **`searchTerm` do pai**
 * (ex.: label vinda da store), não o placeholder animado da SRP.
 */
export interface UseSearchSuggestDialogParentLabelTriggerStateOptions {
	placeholders: () => NewSearchSuggestProps['placeholders'];
	searchTermProp: () => string | undefined;
	modelValue: Ref<Suggestion | undefined>;
	searchTermInternal: Ref<string>;
	getDisplayValue: (
		value: string | number | boolean | Record<string, unknown>
	) => string;
	dynamicPlaceholder: ComputedRef<string>;
	computedPlaceholders: ComputedRef<string[] | string | undefined>;
	/** Fallback do painel quando não há `focusedFieldPlaceholder`. */
	defaultDialogPlaceholder: string;
	focusedFieldPlaceholder: () => string | undefined;
}

export function useSearchSuggestDialogParentLabelTriggerState(
	options: UseSearchSuggestDialogParentLabelTriggerStateOptions
) {
	const normalizedPrimaryPlaceholder = computed(() => {
		const p = options.placeholders();
		if (typeof p === 'string') {
			return p;
		}
		if (Array.isArray(p)) {
			return p[0];
		}
		return undefined;
	});

	const computedTriggerPlaceholder = computed(() => {
		if (normalizedPrimaryPlaceholder.value) {
			return normalizedPrimaryPlaceholder.value;
		}

		if (import.meta.server) {
			const cp = options.computedPlaceholders.value;
			return Array.isArray(cp) ? cp?.[0] || '' : cp || '';
		}

		if (options.modelValue.value?.searchType === 'region') {
			return options.modelValue.value?.result?.subtitle || '';
		}

		return options.defaultDialogPlaceholder;
	});

	const hasSelectionLabel = computed(
		() =>
			!!(options.modelValue.value || options.searchTermInternal.value?.trim())
	);

	const selectedDisplayText = computed(() => {
		if (options.searchTermProp()?.trim()) {
			return options.searchTermProp() as string;
		}
		if (options.modelValue.value) {
			const t = (
				options.getDisplayValue(
					options.modelValue.value.result as unknown as Record<string, unknown>
				) || ''
			).trim();
			if (t) {
				return t;
			}
		}
		if (options.dynamicPlaceholder.value) {
			return options.dynamicPlaceholder.value;
		}
		return (
			options.searchTermInternal.value || computedTriggerPlaceholder.value || ''
		);
	});

	const dialogPanelPlaceholderProps = computed(() => {
		const focused = options.focusedFieldPlaceholder();
		return {
			dialogPlaceholder: focused || options.defaultDialogPlaceholder,
			dialogFocusedPlaceholder: focused
		};
	});

	return {
		normalizedPrimaryPlaceholder,
		computedTriggerPlaceholder,
		hasSelectionLabel,
		selectedDisplayText,
		dialogPanelPlaceholderProps
	};
}
